// frontend/src/utils/pushNotifications.js
// Utilidades para manejar notificaciones push

import axios from 'axios';

// Determinar la URL del API de forma inteligente
const getApiUrl = () => {
    const envApiUrl = process.env.REACT_APP_API_URL;
    // Si estamos en producción (no localhost) pero la URL apunta a localhost, usamos ruta relativa
    if (window.location.hostname !== 'localhost' && envApiUrl && envApiUrl.includes('localhost')) {
        return '/api';
    }
    return envApiUrl || '/api';
};

const API_URL = getApiUrl();

// Clave pública VAPID fija (fallback de seguridad si falla el servidor y el env)
const FALLBACK_VAPID_KEY = 'BLz8yq8_yNlHwplL4hmw9b6nzCc-xib0CQnGcLNr92vmeevd8r4yPrh_KwMnSEgwJ2bhNR84xamnbRibFzqj_Bc';

const getVapidKey = () => {
    return process.env.REACT_APP_VAPID_PUBLIC_KEY || FALLBACK_VAPID_KEY;
};

let CURRENT_VAPID_KEY = getVapidKey();

// Convertir clave pública VAPID a Uint8Array
function urlBase64ToUint8Array(base64String) {
    const padding = '='.repeat((4 - base64String.length % 4) % 4);
    const base64 = (base64String + padding)
        .replace(/-/g, '+')
        .replace(/_/g, '/');

    const rawData = window.atob(base64);
    const outputArray = new Uint8Array(rawData.length);

    for (let i = 0; i < rawData.length; ++i) {
        outputArray[i] = rawData.charCodeAt(i);
    }
    return outputArray;
}

// Registrar Service Worker
export const registerServiceWorker = async () => {
    if ('serviceWorker' in navigator) {
        try {
            const registration = await navigator.serviceWorker.register('/service-worker.js');
            console.log('✅ Service Worker registrado:', registration);
            return registration;
        } catch (error) {
            console.error('❌ Error al registrar Service Worker:', error);
            return null;
        }
    } else {
        console.warn('⚠️  Service Worker no soportado en este navegador');
        return null;
    }
};

// Solicitar permiso para notificaciones
export const requestNotificationPermission = async () => {
    if (!('Notification' in window)) {
        console.warn('⚠️  Notificaciones no soportadas en este navegador');
        return false;
    }

    if (Notification.permission === 'granted') {
        return true;
    }

    if (Notification.permission !== 'denied') {
        try {
            const permission = await Notification.requestPermission();
            return permission === 'granted';
        } catch (error) {
            console.error('Error al solicitar permiso:', error);
            return false;
        }
    }

    return false;
};

// Suscribirse a notificaciones push
export const subscribeToPushNotifications = async (userId) => {
    try {
        // Verificar soporte
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            const isIOS = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
            if (isIOS) {
                throw new Error('En iPhone/iPad, primero debes "Añadir a la pantalla de inicio" (botón Compartir) y abrir la app desde allí para activar notificaciones.');
            }
            throw new Error('Las notificaciones no están soportadas en este navegador o estás en modo incógnito.');
        }

        // Solicitar permiso
        const hasPermission = await requestNotificationPermission();
        if (!hasPermission) {
            throw new Error('Permiso de notificaciones denegado. Por favor, habilítalo en la configuración del navegador.');
        }

        // Obtener registration del Service Worker
        const registration = await navigator.serviceWorker.ready;

        // Intentar obtener clave pública VAPID actualizada del servidor
        try {
            const { data: vapidData } = await axios.get(`${API_URL}/vapid-public-key`);
            if (vapidData && vapidData.publicKey) {
                CURRENT_VAPID_KEY = vapidData.publicKey;
            }
        } catch (err) {
            console.warn('⚠️ No se pudo obtener VAPID key del servidor, usando local:', err.message);
        }

        if (!CURRENT_VAPID_KEY) {
            throw new Error('No se pudo encontrar una clave pública VAPID válida. Contacte al administrador.');
        }

        // Suscribirse
        const subscription = await registration.pushManager.subscribe({
            userVisibleOnly: true,
            applicationServerKey: urlBase64ToUint8Array(CURRENT_VAPID_KEY)
        });

        // Guardar suscripción en el servidor
        await axios.post(`${API_URL}/notifications/subscribe`, {
            userId,
            subscription: subscription.toJSON()
        });

        console.log('✅ Suscrito a notificaciones push');
        return subscription;

    } catch (error) {
        console.error('❌ Error al suscribirse a push notifications:', error);
        throw error;
    }
};

// Cancelar suscripción a notificaciones push
export const unsubscribeFromPushNotifications = async (userId) => {
    try {
        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        if (subscription) {
            await subscription.unsubscribe();

            // Eliminar suscripción del servidor
            await axios.post(`${API_URL}/notifications/unsubscribe`, { userId });

            console.log('✅ Desuscrito de notificaciones push');
            return true;
        }

        return false;
    } catch (error) {
        console.error('❌ Error al desuscribirse de push notifications:', error);
        throw error;
    }
};

// Verificar si está suscrito
export const isPushSubscribed = async () => {
    try {
        if (!('serviceWorker' in navigator) || !('PushManager' in window)) {
            return false;
        }

        const registration = await navigator.serviceWorker.ready;
        const subscription = await registration.pushManager.getSubscription();

        return !!subscription;
    } catch (error) {
        console.error('Error al verificar suscripción:', error);
        return false;
    }
};

// Enviar notificación de prueba
export const sendTestNotification = async (userId) => {
    try {
        await axios.post(`${API_URL}/notifications/send-progress`, {
            userId,
            message: 'Esta es una notificación de prueba',
            progress: 0
        });

        console.log('✅ Notificación de prueba enviada');
        return true;
    } catch (error) {
        console.error('❌ Error al enviar notificación de prueba:', error);
        throw error;
    }
};
